import React, { useEffect, useRef, useState } from 'react'
import { animationFrameScheduler, interval } from 'rxjs'
import { map, takeWhile } from 'rxjs/operators'
import { get, isEmpty, sum, sumBy } from 'lodash-es'
import { subscribe } from 'react-contextual'
import { Helmet } from 'react-helmet'
import classNames from 'classnames'
import { withFormik } from 'formik'
import { concat, filter, join, map as rMap, path, pathOr, pipe, prop } from 'ramda'
import { InvoicesProvider } from '../../../../providers'
import { arrayUtils, dateUtils } from '../../../../../utils'
import { LayoutContext } from 'modules/Layout/LayoutProvider'
import DocumentInfo from 'modules/documents/DocumentInfo/DocumentInfo'
import { CreditNoteButton, CreditNoteContainer, CreditNoteModal } from '../../../../../modules/creditNote'
import { Button, CurrencyField, DimmerLoader, InvoiceCard, Label, LinearProgress, Segment, TextInput } from '../../..'
import PaymentAllocation from './PaymentAllocation/PaymentAllocation'
import { validationSchema } from './validationSchema'
import { approveButtonLabel, canApprovePayments, getBlockingBeneficiaryErrors } from './viewActiveInvoiceUtils'
import NudgeButton from './NudgeButton'
import Accounting from '../../../atoms/Svgs/Accounting'
import styles from './ViewActiveInvoice.module.scss'
import { hashCode } from 'utils/string'
import TextField from '../../../atoms/TextField/TextField'
import EditBeneficiaryAmount from './EditBeneficiaryAmount/EditBeneficiaryAmount'
import FundDistributionFooter from '../../../organisms/FormFragments/FundDistribution/FundDistributionFooter/FundDistributionFooter'
import { $TSFixMe } from 'types/ts-migrate'
import DownloadInvoicePdfButton from 'modules/invoices/DownloadInvoicePdfButton/DownloadInvoicePdfButton'
import CreditNotesList from 'modules/invoices/CreditNotesList/CreditNotesList'
import { transformDepositBeneficiaryForTransferChange } from 'views/components/organisms/FormFragments/FundDistribution/DepositBeneficiary/depositBeneficiaryUtils'
import VATCalculator from 'views/components/molecules/VATCalculator/VATCalculator'
import { existingInvoiceTypes } from 'utils/invoiceTypes'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import DownloadCreditNotePdfButton from 'modules/invoices/DownloadCreditNotePdfButton/DownloadCreditNotePdfButton'

interface ViewActiveInvoiceProps {
  invoice?: $TSFixMe
  canInvoiceBeNudged: (id: string) => boolean
  fetchEasyPayData: (reference: string) => void
  easyPayData?: $TSFixMe
  viewingInvoice?: string
  // Injected by the InvoicesProvider / LayoutContext subscription
  currentInvoice?: $TSFixMe
  getInvoiceTypeByValue: (value: string) => $TSFixMe
  closeInvoice: (id: string, tab: string) => void
  createPayment: (id: string, payments: $TSFixMe[]) => void
  nudge: (ids: string[]) => void
  getLastNudgeTime: (id: string) => string
  updateBeneficiaries: (id: string, beneficiaries: $TSFixMe[]) => void
  partyTags?: $TSFixMe
  getPartyNameById: (id: string) => string
  userRole?: string
  isActiveInvoiceViewLoading?: boolean
  secondaryPanel: $TSFixMe
  getBankingPartyById: (id: string) => $TSFixMe
  fetchPartiesErrors: (ids: string) => void
  isReadOnly?: boolean
  currentAgencyId?: string
  getOwnerAccountFromActiveInvoiceId: (id: string) => $TSFixMe
  getCurrentAgencyGlobalVatEnabled?: boolean
  // Injected by withFormik
  values: $TSFixMe
  setFieldValue: (field: string, value: $TSFixMe, shouldValidate?: boolean) => void
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  submitForm: () => void
  errors: $TSFixMe
}

const ViewActiveInvoice = (props: ViewActiveInvoiceProps): null | React.ReactElement => {
  const {
    currentInvoice: invoice,
    getInvoiceTypeByValue,
    handleSubmit,
    values,
    submitForm,
    canInvoiceBeNudged,
    getLastNudgeTime,
    partyTags,
    errors,
    userRole,
    isActiveInvoiceViewLoading,
    isReadOnly,
    closeInvoice,
    secondaryPanel,
    updateBeneficiaries,
    getPartyNameById,
    getBankingPartyById,
    easyPayData,
    setFieldValue,
    getCurrentAgencyGlobalVatEnabled,
    fetchEasyPayData,
    currentAgencyId,
    getOwnerAccountFromActiveInvoiceId,
    fetchPartiesErrors,
    nudge,
    viewingInvoice,
  } = props

  const [submissionProgress, setSubmissionProgress] = useState(0)
  const [beneficiariesHash, setBeneficiariesHash] = useState<number | null>(null)
  const [fetchingEasyPayData, setFetchingEasyPayData] = useState(false)
  const [currentBeneficiaryIndex, setCurrentBeneficiaryIndex] = useState<number | null>(0)
  const [currentBeneficiaryReference, setCurrentBeneficiaryReference] = useState<string | null>(null)
  const [currentBeneficiaryType, setCurrentBeneficiaryType] = useState<string | null>(null)
  const [currentViewingInvoice, setCurrentViewingInvoice] = useState<string | null>(null)

  const cancelPaymentApproval = useRef(false)
  // Tracks which invoice we've already reconciled deposit routing for, so the on-load
  // repair fires at most once per invoice.
  const reconciledInvoiceRef = useRef<string | null>(null)

  const handleClose = (): void => {
    closeInvoice(values.id, 'active')
  }

  const openSnapshot = (): void => {
    secondaryPanel.open()
  }

  const scrollToCreditNotes = (): void => {
    const element = document.getElementById('creditNotes')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleSave = (newBeneficiaries: any): void => {
    updateBeneficiaries(
      values.id,
      newBeneficiaries.map((b: any) => ({
        ...b,

        beneficiary: {
          ...b.beneficiary,
          value: {
            ...b.beneficiary.value,
            amount: b.beneficiary.value.amount || 0,
          },
        },
      })),
    )
  }

  const getBeneficiaries = (): $TSFixMe => get(props, 'values.beneficiaries', [])

  const sumBeneficiaryAmounts = (): number =>
    sumBy(getBeneficiaries(), (b: any) => {
      const sumValue = parseFloat(get(b, 'beneficiary.value.amount', 0)) || 0
      return sumValue
    })

  const getPaymentAllocationErrors = (): any => {
    const beneficiaryErrors = pipe(
      pathOr([], ['beneficiaries']),
      filter((e: any) => e),
      pathOr(null, [0, 'beneficiary', 'value', 'amountToPay']),
    )(errors)

    /**
     * `general` is reported on its own when the allocation total exceeds available funds
     * or the invoice balance, so it can't be gated on a per-beneficiary error being
     * present too - FundDistribution renders the two independently, and gating it left
     * the over-allocation disabling the approve button with nothing on screen to say why.
     */
    const consolidatedErrors: $TSFixMe = {}
    if (beneficiaryErrors) consolidatedErrors.beneficiaries = beneficiaryErrors
    if (errors?.general) consolidatedErrors.general = errors.general

    return consolidatedErrors
  }

  const updateBeneficiaryAmount = (beneficiaries: any, amount: any, index: number): $TSFixMe =>
    beneficiaries.map((b: any, i: any) => {
      return {
        ...b,
        beneficiary: {
          ...b.beneficiary,
          value: {
            ...b.beneficiary.value,
            amount: i === index ? amount : b.originalAmount,
          },
        },
      }
    })

  const getBeneficiariesForPaymentAllocation = (): $TSFixMe => {
    const creditNoteBeneficiaries = (values?.creditNotes || [])
      .map((creditNote: $TSFixMe) => creditNote.beneficiaries)
      .flat()

    return getBeneficiaries().map((b: any, index: number) => {
      const partyId = pathOr('', ['beneficiary', 'value', 'partyId'], b)
      const preValidationErrors = pathOr([], ['errors'], getBankingPartyById(partyId))

      const beneficiaryEasyPayRef: string = pathOr('', ['beneficiary', 'value', 'easyPayReference'], b)
      const easyPayReferenceData = pathOr('', [beneficiaryEasyPayRef], easyPayData)
      const easyPayError = pathOr('', ['request', 'response_message'], easyPayReferenceData)

      const easyPayErrors =
        easyPayError && easyPayError !== 'Successful' ? [{ key: 'easypay_ref', message: easyPayError }] : []

      const partialBeneficiaryPayment =
        (b.originalAmount === 0 && b.balance === 0) || (b.originalAmount > 0 && b.originalAmount === b.balance)

      const beneficiaryCreditNotes = creditNoteBeneficiaries.filter(
        (creditNoteBeneficiary: $TSFixMe) => b?.beneficiary?.value?.id === creditNoteBeneficiary.beneficiaryId,
      )

      const beneficiaryCreditNotesSum = sumBy(beneficiaryCreditNotes, (creditNote: $TSFixMe) => {
        return creditNote?.amount || 0
      })

      const beneficiaryBalanceWithoutCreditNotes = b.balance + beneficiaryCreditNotesSum

      return {
        ...b,
        beneficiary: {
          ...b.beneficiary,
          value: {
            ...b.beneficiary.value,
            name: getPartyNameById(partyId),
          },
          errors: concat(preValidationErrors, easyPayErrors),
        },
        isRemovable: partialBeneficiaryPayment,
        Meta: () => (
          <>
            <EditBeneficiaryAmount
              amount={b.originalAmount}
              balance={beneficiaryBalanceWithoutCreditNotes}
              onAmountUpdate={(amount: any) => {
                updateBeneficiaries(values.id, updateBeneficiaryAmount(values.beneficiaries, amount, index))
              }}
              isDisabled={isReadOnly}
              isEditable={b.beneficiary.type !== 'DepositBeneficiary'}
            />
            <VATCalculator
              vatApplied={b.beneficiary?.value?.vat}
              disabled={b.beneficiary.type === 'DepositBeneficiary'}
              currencySymbol="R"
              amount={b.beneficiary?.value?.amount}
              onChange={(amountInclVat: number) => {
                updateBeneficiaries(values.id, updateBeneficiaryAmount(values.beneficiaries, amountInclVat, index))
              }}
            />
          </>
        ),
      }
    })
  }

  const handleNewBeneficiary = (b: any, index: number): void => {
    const vatApplied =
      existingInvoiceTypes.includes(values.invoiceType) &&
      b.beneficiary.value.partyTag === 'Agency' &&
      getCurrentAgencyGlobalVatEnabled
        ? true
        : false
    const newBeneficiary = {
      ...b,
      beneficiary: {
        ...b.beneficiary,
        value: {
          ...b.beneficiary.value,
          amount: parseFloat(b.beneficiary?.value?.amount) || 0,
          vat: vatApplied,
        },
      },
      balance: b.beneficiary?.value?.amount || 0,
      originalAmount: parseFloat(b.beneficiary?.value?.amount) || 0, // original amount because `amount` is updated and used to make payments
      availableFunds: get(values, 'customerAvailableFunds', 0),
      invoiceBalance: get(values, 'balance', 0),
    }
    const newBeneficiaries = arrayUtils.insert(index, newBeneficiary, getBeneficiaries())
    setFieldValue('beneficiaries', newBeneficiaries)
    const updateBeneficiariesPayload = updateBeneficiaryAmount(newBeneficiaries, b.beneficiary.value.amount, index)
    updateBeneficiaries(values.id, updateBeneficiariesPayload)
  }

  const handleBeneficiaryAmountChange = (beneficiaryIndex: number, e: any): void => {
    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === beneficiaryIndex)

    setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          amountToPay: e.target.value || 0,
        },
      },
    })
  }

  const updateBeneficiaryReference = (beneficiaryIndex: number, reference: any, type: any): void => {
    const updatedBeneficiaries = values.beneficiaries.map((b: any, i: any) => {
      return {
        ...b,
        beneficiary: {
          ...b.beneficiary,
          value: {
            ...b.beneficiary.value,
            ...(type !== 'EasyPayBeneficiary' && i === beneficiaryIndex ? { reference } : {}),
            ...(type === 'EasyPayBeneficiary' && i === beneficiaryIndex ? { easyPayReference: reference } : {}),
          },
        },
      }
    })

    updateBeneficiaries(values.id, updatedBeneficiaries)
  }

  const handleBeneficiaryReferenceChange = (beneficiaryIndex: number, reference: any, type: any): void => {
    if (type === 'EasyPayBeneficiary') {
      const easyPayReferenceData = pathOr(null, [reference], easyPayData)
      const isEasyPayReferenceDataValid = pathOr(false, ['request', 'success'], easyPayReferenceData)

      if (!easyPayReferenceData || !isEasyPayReferenceDataValid) {
        setFetchingEasyPayData(true)
        setCurrentBeneficiaryIndex(beneficiaryIndex)
        setCurrentBeneficiaryReference(reference)
        setCurrentBeneficiaryType(type)
        fetchEasyPayData(reference)
      } else {
        updateBeneficiaryReference(beneficiaryIndex, reference, type)
      }
    } else {
      updateBeneficiaryReference(beneficiaryIndex, reference, type)
    }

    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === beneficiaryIndex)

    setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          ...(type !== 'EasyPayBeneficiary' ? { reference } : {}),
          ...(type === 'EasyPayBeneficiary' ? { easyPayReference: reference } : {}),
        },
      },
    })
  }

  const handleDepositBeneficiaryTransferChange = (index: number, transfer: any): void => {
    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === index)
    const ownerAccount = getOwnerAccountFromActiveInvoiceId(values?.id)

    // Don't commit an owner-managed toggle (or PUT to the backend) until the owner is
    // resolved — otherwise the deposit would silently fall back to the agency account.
    if (!transfer && !ownerAccount?.partyId) return

    const invoiceTypeObj = getInvoiceTypeByValue(values.invoiceType || '')
    const invoiceTypeName = pathOr('', ['name'], invoiceTypeObj)

    const transformedBeneficiary = transformDepositBeneficiaryForTransferChange(
      beneficiary,
      transfer,
      currentAgencyId || '',
      values?.customerName,
      invoiceTypeName,
      ownerAccount?.partyId,
    )
    setFieldValue(`beneficiaries[${index}]`, transformedBeneficiary)
    updateBeneficiaries(values.id, [transformedBeneficiary])
  }

  const handleBeneficiaryOrderChange = (newOrder: any): void => {
    const updatedBeneficiaries = newOrder
      .map((o: any) => getBeneficiaries()[o])
      .filter((b: any) => b !== null && b !== undefined)
    updateBeneficiaries(values.id, updatedBeneficiaries)
    setBeneficiariesHash(hashCode(JSON.stringify(updatedBeneficiaries)))
  }

  const handleBeneficiaryRemoved = (index: number): void => {
    const newBeneficiaries = arrayUtils.removeByIndex(index, getBeneficiaries())
    setFieldValue('beneficiaries', newBeneficiaries)
    handleSave(newBeneficiaries)
  }

  const handleVatChange = (index: number, vatApplied: any): void => {
    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === index)
    setFieldValue(`beneficiaries[${index}]`, {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          vat: vatApplied,
        },
      },
    })
  }

  const getBeneficiaryPartyErrors = (): void => {
    const beneficiaries = getBeneficiariesForPaymentAllocation()

    const beneficiaryPartyIds = pipe(
      filter((b: any) => path(['beneficiary', 'type'], b) === 'PartyBeneficiary'),
      rMap((b: any) => pathOr('', ['beneficiary', 'value', 'partyId'], b)),
      filter((b: any) => b),
      join(','),
    )(beneficiaries)

    fetchPartiesErrors(beneficiaryPartyIds)
  }

  const updateSubmissionProgress = (onComplete: any): $TSFixMe => {
    getBeneficiaryPartyErrors()

    return interval(100, animationFrameScheduler)
      .pipe(
        map(i => i * 5),
        takeWhile(i => i <= 100),
        takeWhile(() => !cancelPaymentApproval.current),
      )
      .subscribe(i => setSubmissionProgress(i), undefined, onComplete)
  }

  const handleSubmitForm = (formSubmit: any): void => {
    updateSubmissionProgress(() => {
      if (cancelPaymentApproval.current) {
        setSubmissionProgress(0)
        cancelPaymentApproval.current = false
      } else {
        const preValidationErrors = getBlockingBeneficiaryErrors(getBeneficiariesForPaymentAllocation())

        setSubmissionProgress(0)

        if (preValidationErrors.length === 0) {
          formSubmit()
        }
      }
    })
  }

  const handleNudge = (): void => {
    nudge([invoice.id])
  }

  const getBeneficiariesBalanceSum = (): number =>
    pipe(pathOr([], ['beneficiaries']), rMap(prop('balance')), sum)(values)

  const getBeneficiariesAllocationsSum = (): number =>
    pipe(pathOr([], ['beneficiaries']), rMap(prop('originalAmount')), sum)(values)

  const getBeneficiariesTotalAmountToPay = (): number =>
    sumBy(values.beneficiaries, (b: any) => b.beneficiary.value.amountToPay)

  const getUnallocatedAmount = (): number => {
    const balance = pathOr(0, ['values', 'balance'], props)
    return balance - getBeneficiariesBalanceSum()
  }

  const areAllFundsAllocated = (): boolean => getUnallocatedAmount() === 0

  const getBalanceProgress = (): number => {
    switch (values.invoiceStatus) {
      case 'Overdue':
      case 'Short':
      case 'NotSet':
        return areAllFundsAllocated()
          ? 100 - ((values.grossAmount - values.balance) / values.grossAmount) * 100
          : 100 - ((values.grossAmount - getBeneficiariesAllocationsSum()) / values.grossAmount) * 100
      case 'Ready':
        return 100 - ((values.balance - getBeneficiariesAllocationsSum()) / values.balance) * 100
      default:
        return 0
    }
  }

  const buildFundDistributionFooter = (): $TSFixMe => {
    return (
      <FundDistributionFooter
        text={
          values.invoiceStatus === 'Overdue'
            ? 'Amount Overdue'
            : areAllFundsAllocated()
            ? 'Amount to Approve'
            : 'Amount Unallocated'
        }
        amount={areAllFundsAllocated() ? getBeneficiariesTotalAmountToPay() : getUnallocatedAmount()}
        isComplete={
          (values.invoiceStatus === 'Ready' || values.invoiceStatus === 'ready to go') && areAllFundsAllocated()
        }
        progress={{
          value: getBalanceProgress(),
          style:
            values.invoiceStatus === 'Overdue'
              ? 'error'
              : values.invoiceStatus === 'Short' || values.invoiceStatus === 'NotSet'
              ? 'warning'
              : 'default',
        }}
      />
    )
  }

  // componentDidMount
  useEffect(() => {
    setBeneficiariesHash(hashCode(JSON.stringify(get(props, 'currentInvoice.beneficiaries', ''))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate: reset tracked beneficiary state when the viewed invoice changes
  useEffect(() => {
    if (viewingInvoice !== currentViewingInvoice) {
      setCurrentViewingInvoice(viewingInvoice ?? null)
      setCurrentBeneficiaryIndex(null)
      setCurrentBeneficiaryReference(null)
      setCurrentBeneficiaryType(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingInvoice])

  // componentDidUpdate: apply a beneficiary reference once its EasyPay data resolves
  useEffect(() => {
    const easyPayReferenceData = pathOr(null, [currentBeneficiaryReference as string], easyPayData)

    if (easyPayReferenceData && fetchingEasyPayData) {
      const isEasyPayReferenceDataValid = pathOr(false, ['request', 'success'], easyPayReferenceData)
      setFetchingEasyPayData(false)

      if (isEasyPayReferenceDataValid) {
        handleBeneficiaryReferenceChange(
          currentBeneficiaryIndex as number,
          currentBeneficiaryReference,
          currentBeneficiaryType,
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [easyPayData, fetchingEasyPayData, currentBeneficiaryReference, currentBeneficiaryIndex, currentBeneficiaryType])

  // componentDidUpdate: repair deposit beneficiaries persisted in the corrupt state where
  // transfer === false ("Self-managed by Owner") but partyId points at the agency instead of
  // the owner (legacy bug). Since payment approval is routing-agnostic (it sends only
  // beneficiaryId), the stored record must be corrected so approval pays the owner. Re-route
  // to the owner and persist, once per invoice. transfer === true (managed) is left alone —
  // its partyId is the tenant's deposit account (beneficiaryTag 'TenantDepositAccount').
  // Read as a stable string (not the freshly-spread account object) so it can be an effect
  // dependency — the owner resolves asynchronously once the portfolio loads.
  const ownerPartyIdForRepair = getOwnerAccountFromActiveInvoiceId(values?.id)?.partyId
  useEffect(() => {
    const ownerId = ownerPartyIdForRepair
    if (isReadOnly || !ownerId || !values?.id || reconciledInvoiceRef.current === values.id) return

    const beneficiaries = values.beneficiaries || []
    const isMisroutedDeposit = (b: any): boolean =>
      b?.beneficiary?.type === 'DepositBeneficiary' &&
      b?.beneficiary?.value?.transfer === false &&
      b?.beneficiary?.value?.partyId !== ownerId

    if (!beneficiaries.some(isMisroutedDeposit)) return

    reconciledInvoiceRef.current = values.id
    const repairInvoiceTypeName = pathOr('', ['name'], getInvoiceTypeByValue(values.invoiceType || ''))
    const repair = (b: any) =>
      transformDepositBeneficiaryForTransferChange(
        b,
        false,
        currentAgencyId || '',
        values?.customerName,
        repairInvoiceTypeName,
        ownerId,
      )
    const repairedDeposits = beneficiaries.filter(isMisroutedDeposit).map(repair)
    const repairedAll = beneficiaries.map((b: any) => (isMisroutedDeposit(b) ? repair(b) : b))
    setFieldValue('beneficiaries', repairedAll)
    updateBeneficiaries(values.id, repairedDeposits)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.id, values?.beneficiaries, ownerPartyIdForRepair, isReadOnly])

  const isLoaded = !isEmpty(values) || invoice

  const styleNames = classNames('root')

  const invoiceTypeObj = getInvoiceTypeByValue(values.invoiceType || '')
  const invoiceTypeName = pathOr('', ['name'], invoiceTypeObj)

  const creditNotes = values.creditNotes || []

  return (
    <div className={styleNames}>
      {isLoaded && (
        <Helmet>
          <title>
            reOS | Invoices | Active | {`${values.customerName as string} - ${values.invoiceType as string}`}
          </title>
        </Helmet>
      )}
      <InvoiceCard
        invoice={{ ...invoice, invoiceNumber: `INV-${pathOr('', ['invoiceNumber'], invoice) as string}` }}
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; invoice: any; contextua... Remove this comment to see the full error message
        contextualMenuOptions={[]}
        onClose={handleClose}
        headerBefore={
          !isEmpty(creditNotes) ? (
            <>
              <Button className={styles['snapshot-button']} size="xs" ghost onClick={openSnapshot}>
                More Info
              </Button>
              <Button className={styles['credit-notes']} size="xs" ghost onClick={scrollToCreditNotes}>
                Credit Notes
              </Button>
            </>
          ) : (
            <Button className={styles['snapshot-button']} size="xs" ghost onClick={openSnapshot}>
              More Info
            </Button>
          )
        }
      >
        <DimmerLoader loading={isActiveInvoiceViewLoading} style={{ minHeight: '300px' }}>
          <section className={styles['invoice-data']}>
            <form onSubmit={handleSubmit}>
              <fieldset disabled={isReadOnly}>
                {isLoaded && invoice && (
                  <>
                    <Segment.Group>
                      {values.invoiceStatus === 'Overdue' && (
                        <div className={styles['label-overdue']}>
                          <Label size="md">Overdue</Label>
                        </div>
                      )}
                      {/** @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element[]; horizontal: true; onC... Remove this comment to see the full error message */}
                      <Segment horizontal onClick={openSnapshot} style={{ flexWrap: 'nowrap' }}>
                        <Segment.Label
                          icon={<Accounting />}
                          text={invoiceTypeName}
                          secondaryText={
                            typeof values.dueDate !== 'undefined'
                              ? `Due ${dateUtils.timeAgo(new Date(values.dueDate))}`
                              : ''
                          }
                        />
                        <div className={styles.amount}>
                          <CurrencyField
                            disabled
                            includeVatControl
                            vatApplied={values.vatAmount > 0}
                            name="amount"
                            placeholder="Enter amount"
                            value={values.grossAmount}
                          />
                        </div>
                      </Segment>
                      {values.description && (
                        <Segment horizontal>
                          <TextField
                            inputComponent={
                              <TextInput type={TextFieldTypes.text} value={values.description} disabled />
                            }
                            label="Description"
                          />
                        </Segment>
                      )}
                    </Segment.Group>

                    {!isActiveInvoiceViewLoading && (
                      <PaymentAllocation
                        errors={getPaymentAllocationErrors()}
                        invoiceId={invoice.id}
                        invoiceType={getInvoiceTypeByValue(values.invoiceType)}
                        invoiceStatus={values.invoiceStatus}
                        balance={values.balance}
                        grossAmount={values.grossAmount}
                        availableFunds={values.customerAvailableFunds || 0}
                        beneficiaries={getBeneficiariesForPaymentAllocation()}
                        onNewBeneficiaryAdded={handleNewBeneficiary}
                        onNewBeneficiaryAmountChange={handleBeneficiaryAmountChange}
                        onBeneficiaryReferenceChange={handleBeneficiaryReferenceChange}
                        onDepositBeneficiaryTransferChange={handleDepositBeneficiaryTransferChange}
                        onBeneficiariesOrderChange={handleBeneficiaryOrderChange}
                        onBeneficiaryRemoved={handleBeneficiaryRemoved}
                        onVatChange={handleVatChange}
                        partyTags={partyTags}
                        beneficiariesKey={beneficiariesHash}
                        Footer={buildFundDistributionFooter}
                        inputTooltipText="Amount to pay this beneficiary"
                        unallocatedAmount={getUnallocatedAmount()}
                        portfolioId={values.portfolioId}
                      />
                    )}

                    <Segment.Group>
                      <Segment horizontal>
                        <Segment.Label text="Credit Notes" />
                        <CreditNoteContainer>
                          <div id="creditNotes"></div>
                          <CreditNoteButton className={styles['credit-note-button']} invoiceId={invoice.id} />
                          <CreditNoteModal />
                        </CreditNoteContainer>
                      </Segment>
                      <CreditNotesList creditNotes={creditNotes} />
                      {!isEmpty(creditNotes) && (
                        <div className={styles['download-credit-note']}>
                          <DownloadCreditNotePdfButton invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} />
                        </div>
                      )}
                    </Segment.Group>

                    <Segment.Group>
                      <Segment horizontal state={values.invoiceStatus === 'Overdue' ? 'danger' : 'default'}>
                        <div className={styles['overdue-actions']}>
                          {(values.invoiceStatus === 'Overdue' || values.invoiceStatus === 'Short') && (
                            <>
                              <span style={{ width: '100%' }}>
                                You can only nudge once every 24 hours.
                                {!canInvoiceBeNudged(invoice.id) &&
                                  ` Last nudge sent on ${getLastNudgeTime(invoice.id)}`}
                              </span>
                              <NudgeButton
                                canInvoiceBeNudged={canInvoiceBeNudged(invoice.id)}
                                handleNudge={handleNudge}
                              />
                              {/* <Button size='sm' secondary>Send notice</Button> */}
                            </>
                          )}
                        </div>
                      </Segment>
                    </Segment.Group>
                  </>
                )}
              </fieldset>

              {isLoaded && invoice && (
                <>
                  {invoice?.id && (
                    <Segment.Group>
                      <div className={styles['files-section']}>
                        <DocumentInfo className="invoice" classId={invoice.id} modalTitle={invoice.customerName} />
                      </div>
                    </Segment.Group>
                  )}

                  <footer>
                    <div className={styles['form-footer']}>
                      <div className={styles['action-buttons']}>
                        <DownloadInvoicePdfButton invoiceId={invoice.id} invoiceNumber={invoice.invoiceNumber} />
                        {userRole === 'Owner' && (
                          <>
                            {submissionProgress > 0 ? (
                              <Button
                                secondary
                                onClick={() => {
                                  cancelPaymentApproval.current = true
                                }}
                              >
                                Cancel
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                onClick={() => handleSubmitForm(submitForm)}
                                disabled={
                                  !canApprovePayments(values) || !isEmpty(errors) || sumBeneficiaryAmounts() === 0
                                }
                                ghost={!canApprovePayments(values)}
                              >
                                {approveButtonLabel(values)}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <LinearProgress percent={submissionProgress} strokeColor="#128269" strokeWidth={0.8} />
                  </footer>
                </>
              )}
            </form>
          </section>
        </DimmerLoader>
      </InvoiceCard>
    </div>
  )
}

export default subscribe(
  [InvoicesProvider, LayoutContext],
  (
    {
      currentInvoice,
      // toggleEdit,
      getInvoiceTypeByValue,
      closeInvoice,
      createPayment,
      nudge,
      canInvoiceBeNudged,
      getLastNudgeTime,
      updateBeneficiaries,
      partyTags,
      agencyBankDetails,
      getPartyBankDetails,
      getPartyNameById,
      userRole,
      fetchPortfolio,
      isActiveInvoiceViewLoading,
      getBankingPartyById,
      fetchPartiesErrors,
      fetchEasyPayData,
      easyPayData,
      viewingInvoice,
      isReadOnly,
      currentAgencyId,
      getOwnerAccountFromActiveInvoiceId,
      getPartyAccount,
      getCurrentAgencyGlobalVatEnabled,
    }: any,
    { secondaryPanel }: any,
  ) => ({
    currentInvoice,
    // toggleEdit,
    getInvoiceTypeByValue,
    closeInvoice,
    createPayment,
    nudge,
    canInvoiceBeNudged,
    getLastNudgeTime,
    updateBeneficiaries,
    partyTags,
    agencyBankDetails,
    getPartyBankDetails,
    getPartyNameById,
    userRole,
    fetchPortfolio,
    isActiveInvoiceViewLoading,
    secondaryPanel,
    getBankingPartyById,
    fetchPartiesErrors,
    fetchEasyPayData,
    easyPayData,
    viewingInvoice,
    isReadOnly,
    currentAgencyId,
    getOwnerAccountFromActiveInvoiceId,
    getPartyAccount,
    getCurrentAgencyGlobalVatEnabled,
  }),
)(
  withFormik({
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    /**
     * Without this, `errors` is empty until the first change or blur, so the Approve
     * Payments button below is enabled on an over-allocated invoice (a Short one, where
     * the default allocation exceeds available funds) and the approval can be submitted
     * before validation has ever run. Formik also re-validates after each
     * `enableReinitialize` reset only when this is set - otherwise every invoice refetch
     * clears the errors and re-enables the button.
     */
    validateOnMount: true,
    enableReinitialize: true,
    mapPropsToValues: (props: $TSFixMe) => {
      const invoice = get(props, 'currentInvoice', {})
      const beneficiaries = get(invoice, 'beneficiaries', [])

      return {
        ...invoice,
        /**
         * Add availableFunds to beneficiaries because yup doesn't support accessing parents higher than 1 level
         * @link https://github.com/jquense/yup/issues/225#issuecomment-404910170
         */
        beneficiaries: beneficiaries.map((b: any) => ({
          ...b,
          originalAmount: b.beneficiary.value.amount,
          // Add to beneficiary for validation purposes. Validation object doesn't have access to outer scope
          availableFunds: get(invoice, 'customerAvailableFunds', 0),
          // Add to beneficiary for validation purposes. Validation object doesn't have access to outer scope
          invoiceBalance: get(invoice, 'balance', 0),
          beneficiary: {
            ...b.beneficiary,
            value: {
              ...b.beneficiary.value,
              balance: b.balance,
              amountToPay: b.balance,
            },
          },
        })),
      }
    },
    handleSubmit: (values: $TSFixMe, { props }: $TSFixMe) => {
      const { id, beneficiaries } = values
      props.createPayment(
        id,
        beneficiaries.map(({ beneficiary }: any) => ({
          beneficiaryId: beneficiary.value.id,
          amount: beneficiary.value.amountToPay,
        })),
      )

      const isOutstandingAmountPaid =
        sumBy(beneficiaries, (b: any) => parseFloat(get(b, 'balance', 0)) || 0) ===
        sumBy(beneficiaries, (b: any) => parseFloat(get(b, 'beneficiary.value.amount', 0)) || 0)

      if (isOutstandingAmountPaid) {
        props.closeInvoice(values.id, 'active')
      }
    },
  })(ViewActiveInvoice),
)
